import { getServingsPerMealByFoodType } from 'common/MealPlanUtils';
import { shefSpiceText } from 'common/SpiceLevels';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { UpsellTier } from 'src/gqlReactTypings.generated.d';
import { MealPlanFoodItem } from 'src/pages/consumer/meal-plans/checkout/types';
import { FoodItemTagChips } from 'src/pages/consumer/meal-plans/components/Chips/FoodItemTagChips';
import { UpsellLabelChip } from 'src/pages/consumer/meal-plans/components/Chips/UpsellChips';
import { FoodItemRatings } from 'src/pages/consumer/meal-plans/components/FoodItemRatings';
import { ShefHandwrittenName } from 'src/pages/consumer/meal-plans/components/ShefHandwrittenName';
import { DIETARY_TAG_DISPLAY_ORDER, dietaryTagToFoodItemFilter } from 'src/shared/utils/diet-utils';
import { DisplayedFoodItemTags } from 'src/shared/utils/IconUtils';
import Chip, { ChipColor } from 'web-common/src/shared/design-system/components/Chip';
import { GradientDivider } from 'web-common/src/shared/design-system/components/Divider/GradientDivider';
import { ExpandableText } from 'web-common/src/shared/design-system/components/ExpandableText';
import { DietaryTags as DietaryTagsEnum } from 'web-common/src/shared/Enums';
import { DietaryTags } from './DietaryTags';
import { SpiceTag } from './SpiceTag';

export type DishModalInfoFoodItem = DisplayedFoodItemTags &
  Pick<
    MealPlanFoodItem,
    | 'id'
    | 'spiceLevel'
    | 'portion'
    | 'description'
    | 'isSeedOilFree'
    | 'tags'
    | 'ingredientsFlagged'
    | 'customIngredientsFlagged'
    | 'upsell'
    | 'mealPlanFoodType'
  > &
  Partial<Pick<MealPlanFoodItem, 'averageRating' | 'ratingCount' | 'shef'>>;

interface DishModalInfoProps {
  foodItem: DishModalInfoFoodItem;
  servingsPerMeal?: number;
}

export const DishModalInfo: React.FC<DishModalInfoProps> = ({ foodItem, servingsPerMeal }) => {
  const ORGANIC_UPSELL_TIERS = new Set([
    UpsellTier.OrganicVegetarian,
    UpsellTier.FullOrganic,
    UpsellTier.OrganicProtein,
  ]);
  const hasOrganicUpsell = foodItem.upsell?.tier && ORGANIC_UPSELL_TIERS.has(foodItem.upsell.tier);

  const dietaryTags = useMemo(
    () =>
      DIETARY_TAG_DISPLAY_ORDER.filter(
        (dietaryTag) =>
          dietaryTagToFoodItemFilter[dietaryTag](foodItem) &&
          !(hasOrganicUpsell && dietaryTag === DietaryTagsEnum.ORGANIC)
      ),
    [foodItem, hasOrganicUpsell]
  );

  /**
   * ========================================================
   * Render components
   * ========================================================
   */

  const ratingsAndDietaryTagsRow = (
    <div className='no-scrollbar my-1.5 flex items-center gap-2 overflow-x-auto'>
      {!isNil(foodItem.averageRating) && !isNil(foodItem.ratingCount) && (
        <FoodItemRatings
          averageRating={foodItem.averageRating}
          ratingCount={foodItem.ratingCount}
          foodItemId={foodItem.id}
        />
      )}
      {foodItem.upsell && <UpsellLabelChip upsell={foodItem.upsell} applyFullHeight />}
      <FoodItemTagChips isSeedOilFree={foodItem.isSeedOilFree} tags={foodItem.tags} />
      <DietaryTags
        dietaryTags={dietaryTags}
        ingredientsFlagged={foodItem.ingredientsFlagged}
        customIngredientsFlagged={foodItem.customIngredientsFlagged}
      />
    </div>
  );

  const portionCopy = useMemo(() => {
    if (!isNil(servingsPerMeal)) {
      const numServings = getServingsPerMealByFoodType(foodItem.mealPlanFoodType, servingsPerMeal);
      return `${numServings} serving${numServings !== 1 ? 's' : ''}`;
    }
    return foodItem.portion;
  }, [servingsPerMeal, foodItem.mealPlanFoodType, foodItem.portion]);

  const portionRow = (
    <div className='flex items-center justify-between py-4'>
      <span className='text-caption-700-caps text-eggplant'>Portion</span>
      <Chip color={ChipColor.CANTELOUPE_50} copy={portionCopy} />
    </div>
  );

  const spiceRow = (
    <div className='flex items-center justify-between py-4'>
      <span className='text-caption-700-caps text-eggplant'>Spice Level</span>
      <div className='flex items-center gap-2'>
        <SpiceTag spiceLevel={foodItem.spiceLevel} size={24} />
        <span className='text-body-400 text-eggplant-500'>{shefSpiceText.get(foodItem.spiceLevel) ?? 'Not Spicy'}</span>
      </div>
    </div>
  );

  return (
    <div className='w-full bg-white text-left'>
      {/* Rating and dietary tags */}
      {ratingsAndDietaryTagsRow}

      {/* Shef name */}
      {foodItem.shef && (
        <ShefHandwrittenName
          name={foodItem.shef.publicName}
          nameSize='text-h4'
          className='text-body-600 text-eggplant'
        />
      )}

      {/* Food item description */}
      {foodItem.description && (
        <div className='mb-2.5 mt-4 text-body-400 text-eggplant'>
          <ExpandableText numberOfLines={3} text={foodItem.description} expandText='read more' />
        </div>
      )}

      {/* Portion */}
      {portionCopy && (
        <>
          {portionRow}
          <GradientDivider />
        </>
      )}

      {/* Spice Level */}
      {spiceRow}
      <GradientDivider />
    </div>
  );
};
